import { BalanceData } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { Fnv1a64 } from "../maths/fnv1a64";

/**
 * An event-mode run (MP-21, docs/MODES.md §5): a Boss Week tier, Race Week's road or an Odyssey map. Like the Iron,
 * Endless and Daily Road blocks it is a run input carried in the replay record (`modifiers.event`) and hashed only on
 * a run that has it, so every golden recorded without it keeps its hash. What each field does in the sim:
 *
 * - `stages`: the borrowed layout's first `stages` stages, its boss closing the last (as Daily Road; sim/runModes.ts);
 * - `countMult`: every group's count per stage × this, rounded up (Boss Week's tiers; sim/waveSchedule.ts);
 * - `armour`: added to every raider's armour against physical hits (Boss Week's tiers; systems/combat.ts);
 * - `towers`: the only tower archetypes the draft deals (Odyssey's deck), as Iron's are; empty = the player's own;
 * - `lives`: the Fortress HP the run starts with (Odyssey's shared pool carried from map to map); 0 = full.
 *
 * An event run banks no battle chest and rolls no rare spawn: what it pays is the mode's (meta/eventModes.ts).
 */
export type EventRunMode = "boss" | "race" | "odyssey";
export const EVENT_RUN_MODES: readonly EventRunMode[] = ["boss", "race", "odyssey"];

export interface EventRunRule {
  readonly mode: EventRunMode;
  /** The rotation week the run belongs to: its Monday as a UTC day number. */
  readonly week: number;
  /** Boss Week's tier (1–5, 6 = Elite), the Odyssey's map (1–3); 1 for Race Week. */
  readonly step: number;
  readonly stages: number;
  readonly countMult: Fix64;
  readonly armour: Fix64;
  readonly towers: readonly number[];
  readonly lives: number;
}

/** Hash tag of the event block (written only when the run has one). */
const EVENT_HASH_TAG = 0xe7e0;

export function hashEventRule(h: Fnv1a64, r: EventRunRule): void {
  h.writeInt(EVENT_HASH_TAG);
  h.writeInt(EVENT_RUN_MODES.indexOf(r.mode));
  h.writeInt(r.week); h.writeInt(r.step); h.writeInt(r.stages);
  h.writeRaw(r.countMult.raw); h.writeRaw(r.armour.raw);
  h.writeInt(r.towers.length);
  for (const t of r.towers) h.writeInt(t);
  h.writeInt(r.lives);
}

/** The event block as a replay record stores it: Fix64 raw, towers by id. */
export interface EventRunJson {
  mode: string; week: number; step: number; stages: number; countMult: string; armour: string; towers: string[]; lives: number;
}

export function eventRuleToJson(balance: BalanceData, r: EventRunRule): EventRunJson {
  return { mode: r.mode, week: r.week, step: r.step, stages: r.stages, countMult: r.countMult.raw.toString(), armour: r.armour.raw.toString(), towers: r.towers.map((t) => balance.towers[t].id), lives: r.lives };
}

const isNat = (v: unknown, min: number): boolean => Number.isInteger(v) && (v as number) >= min;
const isRaw = (v: unknown): v is string => typeof v === "string" && /^\d+$/.test(v);

/** Structural check of a stored event block (tower ids are resolved on use). */
export function isEventRunJson(v: unknown): v is EventRunJson {
  if (typeof v !== "object" || v === null) return false;
  const j = v as Partial<EventRunJson>;
  return (EVENT_RUN_MODES as readonly string[]).includes(j.mode as string) && isNat(j.week, 0) && isNat(j.step, 1) && isNat(j.stages, 1)
    && isRaw(j.countMult) && isRaw(j.armour) && Array.isArray(j.towers) && j.towers.every((t) => typeof t === "string") && isNat(j.lives, 0);
}

/** Parses a stored event block; undefined for anything malformed or naming a tower this build does not have. */
export function eventRuleFromJson(balance: BalanceData, v: unknown): EventRunRule | undefined {
  if (!isEventRunJson(v) || BigInt(v.countMult) <= 0n) return undefined;
  const towers = v.towers.map((id) => balance.towers.findIndex((t) => t.id === id && !t.isBase));
  if (towers.some((t) => t < 0)) return undefined;
  return {
    mode: v.mode as EventRunMode, week: v.week, step: v.step, stages: v.stages,
    countMult: Fix64.fromRaw(BigInt(v.countMult)), armour: Fix64.fromRaw(BigInt(v.armour)), towers, lives: v.lives,
  };
}

/** Two event blocks are the same run rules. */
export function sameEventRule(a: EventRunRule | undefined, b: EventRunRule | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  return a.mode === b.mode && a.week === b.week && a.step === b.step && a.stages === b.stages && a.countMult.eq(b.countMult) && a.armour.eq(b.armour)
    && a.lives === b.lives && a.towers.length === b.towers.length && a.towers.every((t, i) => t === b.towers[i]);
}
